import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@material-ui/core";
import {
  PresetItem,
  PRESET_TYPES,
  FieldRepresentativePreset,
  CompanyInfoPreset,
  ProjectInfoPreset,
} from "../../models/PresetItem";
import WarrantyPeriodModal from "./WarrantyPeriodModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    preset: Omit<PresetItem, "id" | "createdAt">,
    editMode?: { id: string; createdAt: string }
  ) => void;
  presetType: "fieldRepresentative" | "companyInfo" | "projectInfo";
  editingPreset?: PresetItem | null;
  presets: PresetItem[];
  onEditPreset: (preset: PresetItem | null) => void;
  onDeletePreset: (preset: PresetItem) => void;
  defaultPresets?: {
    companyInfo?: PresetItem;
    fieldRepresentative?: PresetItem;
    projectInfo?: PresetItem;
  };
  onSetDefaultPreset?: (
    type: "companyInfo" | "fieldRepresentative" | "projectInfo",
    preset?: PresetItem
  ) => void;
  onClearDefaultPreset?: (
    type: "companyInfo" | "fieldRepresentative" | "projectInfo"
  ) => void;
  // 로컬스토리지 업데이트 함수
  getDefaultPresetsFromStorage?: () => any;
  saveDefaultPresetsToStorage?: (defaultPresets: any) => void;
}

const PresetModal = ({
  isOpen,
  onClose,
  onSave,
  presetType,
  editingPreset,
  presets,
  onEditPreset,
  onDeletePreset,
  defaultPresets,
  onSetDefaultPreset,
  onClearDefaultPreset,
  getDefaultPresetsFromStorage,
  saveDefaultPresetsToStorage,
}: Props) => {
  const [activeTab, setActiveTab] = useState<
    "fieldRepresentative" | "companyInfo" | "projectInfo"
  >("companyInfo");
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [forceListMode, setForceListMode] = useState(false); // 강제 목록 모드
  const [formData, setFormData] = useState<any>({});
  const [originalResidentNumber, setOriginalResidentNumber] =
    useState<string>(""); // 주민번호 원본 값 저장

  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [selectedCompanyInfo, setSelectedCompanyInfo] =
    useState<PresetItem | null>(null);
  const [dateError, setDateError] = useState<string>(""); // 날짜 에러 메시지

  const presetConfig = PRESET_TYPES[activeTab];

  // 주민번호 표시값 계산 함수 (마스킹 제거)
  const getDisplayResidentNumber = () => {
    if (!originalResidentNumber) {
      return formData.residentNumber || "";
    }

    // 항상 원본 주민번호 표시 (하이픈 포함)
    if (originalResidentNumber.length <= 6) {
      return originalResidentNumber;
    } else if (originalResidentNumber.length === 7) {
      return (
        originalResidentNumber.substring(0, 6) +
        "-" +
        originalResidentNumber.substring(6)
      );
    } else {
      return (
        originalResidentNumber.substring(0, 6) +
        "-" +
        originalResidentNumber.substring(6)
      );
    }
  };

  // 연결된 회사정보를 찾는 함수 (메모이제이션)
  const getLinkedCompanyName = useMemo(() => {
    const companyMap = new Map();
    const companies = presets.filter((p) => p.type === "companyInfo");

    companies.forEach((company) => {
      const splitId = company.id.split("-")[0];
      companyMap.set(splitId, company.name);
    });

    return (companyId: string | number) => {
      const searchKey = String(companyId);
      return companyMap.get(searchKey) || "회사정보 없음";
    };
  }, [presets]);

  useEffect(() => {
    // activeTab이 변경될 때마다 formData 초기화 (편집모드가 아니거나 forceListMode일 때)
    if (!editingPreset || forceListMode) {
      const initialData: any = {};
      PRESET_TYPES[activeTab].fields.forEach((field) => {
        initialData[field.key] = "";
      });

      setFormData(initialData);
      setIsEditing(false);
      if (!forceListMode) {
        setShowForm(false);
      }
      setOriginalResidentNumber("");
      setDateError(""); // 날짜 에러 메시지 초기화
    }
  }, [activeTab, editingPreset, forceListMode]);

  // selectedCompanyInfo가 변경될 때는 companyId만 업데이트 (폼은 그대로 유지)
  useEffect(() => {
    if (!editingPreset && showForm && selectedCompanyInfo) {
      if (activeTab === "fieldRepresentative" || activeTab === "projectInfo") {
        setFormData((prevData: any) => ({
          ...prevData,
          companyId: selectedCompanyInfo.id.split("-")[0],
        }));
      }
    }
  }, [selectedCompanyInfo, showForm, activeTab, editingPreset]);

  useEffect(() => {
    if (editingPreset && !forceListMode) {
      setActiveTab(editingPreset.type);

      // 편집 모드에서 날짜 형식 변환 (yyyy년 mm월 dd일 → yyyy-mm-dd for input[type="date"])
      const convertedData = { ...editingPreset.data };
      const dateFields = [
        "contractDate",
        "startDate",
        "completionDate",
        "expectedCompletionDate",
        "claimDate",
        "birthDate",
        "registrationDate",
        "year",
      ];

      dateFields.forEach((field) => {
        const convertedDataAny = convertedData as any;
        if (
          convertedDataAny[field] &&
          typeof convertedDataAny[field] === "string"
        ) {
          const dateValue = convertedDataAny[field] as string;
          // yyyy년 mm월 dd일 형식을 yyyy-mm-dd로 변환
          if (
            dateValue.includes("년") &&
            dateValue.includes("월") &&
            dateValue.includes("일")
          ) {
            const yearMatch = dateValue.match(/(\d{4})년/);
            const monthMatch = dateValue.match(/(\d{1,2})월/);
            const dayMatch = dateValue.match(/(\d{1,2})일/);

            if (yearMatch && monthMatch && dayMatch) {
              const year = yearMatch[1];
              const month = monthMatch[1].padStart(2, "0");
              const day = dayMatch[1].padStart(2, "0");
              convertedDataAny[field] = `${year}-${month}-${day}`;
            }
          }
        }
      });

      setFormData(convertedData);
      setIsEditing(true);
      setShowForm(true);

      // 편집 모드에서 주민번호 원본 값 설정 (마스킹 제거)
      if (editingPreset.type === "fieldRepresentative") {
        const data = editingPreset.data as any;
        // residentNumber가 원본 값이므로 그것을 사용
        if (data.residentNumber) {
          // 원본값에서 숫자만 추출해서 저장
          const numbersOnly = data.residentNumber.replace(/[^0-9]/g, "");
          setOriginalResidentNumber(numbersOnly);

          // 원본값을 그대로 폼 데이터에 설정 (마스킹 없이)
          let originalValue = "";
          if (numbersOnly.length <= 6) {
            originalValue = numbersOnly;
          } else if (numbersOnly.length === 7) {
            originalValue =
              numbersOnly.substring(0, 6) + "-" + numbersOnly.substring(6);
          } else {
            originalValue =
              numbersOnly.substring(0, 6) + "-" + numbersOnly.substring(6);
          }

          // formData 업데이트를 위해 별도 처리 (useEffect 이후에 처리됨)
        }

        // 현장대리인 편집 시 연결된 회사정보 찾기
        if (data.companyId) {
          const companyPreset = presets.find(
            (p) =>
              p.type === "companyInfo" &&
              p.id.split("-")[0] === String(data.companyId)
          );
          setSelectedCompanyInfo(companyPreset || null);
        }
      } else if (editingPreset.type === "projectInfo") {
        const data = editingPreset.data as any;
        // 공사정보 편집 시 연결된 회사정보 찾기
        console.log("🏗️ 공사정보 편집 모드 - companyId:", data.companyId);
        console.log(
          "📋 사용 가능한 회사정보 프리셋:",
          presets.filter((p) => p.type === "companyInfo")
        );

        if (data.companyId) {
          console.log("🔍 검색 조건 - data.companyId:", data.companyId);
          console.log(
            "🔍 회사정보 프리셋 ID들:",
            presets
              .filter((p) => p.type === "companyInfo")
              .map((p) => ({
                fullId: p.id,
                splitId: p.id.split("-")[0],
                name: p.name,
              }))
          );

          const companyPreset = presets.find(
            (p) =>
              p.type === "companyInfo" &&
              p.id.split("-")[0] === String(data.companyId)
          );
          console.log("🎯 찾은 회사정보 프리셋:", companyPreset);
          setSelectedCompanyInfo(companyPreset || null);
        } else {
          console.log(
            "⚠️ companyId가 없어서 회사정보를 자동 선택할 수 없습니다"
          );
          setSelectedCompanyInfo(null);
        }
      }
    }
  }, [editingPreset, presets, forceListMode]);

  // editingPreset 변경 시 폼 모드 결정
  useEffect(() => {
    // editingPreset이 null이 되면 forceListMode 해제
    if (!editingPreset && forceListMode) {
      setForceListMode(false);
      return;
    }

    // forceListMode가 활성화되어 있으면 무시
    if (forceListMode) {
      return;
    }

    if (editingPreset) {
      setShowForm(true);
      setIsEditing(true);
    } else if (isOpen) {
      setShowForm(false);
      setIsEditing(false);
    }
  }, [editingPreset, isOpen, forceListMode]);

  // 모달이 닫힐 때 모든 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setShowForm(false);
      setIsEditing(false);
      setForceListMode(false); // forceListMode도 초기화
      setDateError("");
      setOriginalResidentNumber("");

      // formData 초기화
      const initialData: any = {};
      PRESET_TYPES[activeTab].fields.forEach((field) => {
        initialData[field.key] = "";
      });
      setFormData(initialData);
    }
  }, [isOpen, activeTab]);

  // 편집 모드에서 주민번호 원본값 처리 (마스킹 제거)
  useEffect(() => {
    if (formData.residentNumber && originalResidentNumber) {
      // 원본값을 그대로 표시 (마스킹 없이)
      let originalValue = "";
      if (originalResidentNumber.length <= 6) {
        originalValue = originalResidentNumber;
      } else if (originalResidentNumber.length === 7) {
        originalValue =
          originalResidentNumber.substring(0, 6) +
          "-" +
          originalResidentNumber.substring(6);
      } else {
        originalValue =
          originalResidentNumber.substring(0, 6) +
          "-" +
          originalResidentNumber.substring(6);
      }

      // 현재 formData와 다르면 업데이트
      if (formData.residentNumber !== originalValue) {
        setFormData((prev: any) => ({
          ...prev,
          residentNumber: originalValue,
        }));
      }
    }
  }, [originalResidentNumber, formData.residentNumber]);

  // 하자담보 책임기간 데이터
  const warrantyPeriodData = [
    {
      category: "건설업법에 의한 건설공사의 공종별 하자담보 책임기간",
      items: [
        {
          subCategory: "가. 교량",
          details: [
            {
              description:
                "(1) 교각과 교각사이의 간격이 50m 이상이거나 길이 500m 이상인 교량의 철근콘크리트 또는 철골구조부",
              period: 10,
            },
            {
              description:
                "(2) 길이 500m 미만인 교량의 철근콘크리트 또는 철골구조부",
              period: 7,
            },
            {
              description:
                "(3) 교량중 교면포장ㆍ이음부ㆍ난간시설 등 (1) 및 (2) 외의 공종",
              period: 2,
            },
          ],
        },
        {
          subCategory: "나. 터널",
          details: [
            {
              description:
                "(1) 터널(지하철을 포함)의 철근콘크리트 또는 철골구조부",
              period: 10,
            },
            { description: "(2) (1) 외의 시설", period: 5 },
          ],
        },
        {
          subCategory: "다. 철도",
          details: [
            {
              description:
                "(1) 교량 및 터널을 제외한 철도시설 중 철근콘크리트 또는 철골구조부",
              period: 7,
            },
            { description: "(2) (1) 외의 시설", period: 5 },
          ],
        },
        {
          subCategory: "라. 공항 및 삭도",
          details: [
            { description: "(1) 철근콘크리트 또는 철골구조부", period: 7 },
            { description: "(2) (1) 외의 시설", period: 5 },
          ],
        },
        {
          subCategory: "마. 항만ㆍ방파제ㆍ사방 또는 간척",
          details: [
            { description: "(1) 철근콘크리트 또는 철골구조부", period: 7 },
          ],
        },
        {
          subCategory: "바. 도로(암거ㆍ측구 및 포장을 포함)",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "사. 댐",
          details: [
            { description: "(1) 본체 또는 여수로 부분", period: 10 },
            { description: "(2) (1) 외의 시설", period: 5 },
          ],
        },
        {
          subCategory: "아. 상ㆍ하수도",
          details: [
            { description: "(1) 철근콘크리트 또는 철골구조부", period: 7 },
            { description: "(2) 관로매설 또는 기지설치", period: 3 },
          ],
        },
        {
          subCategory: "자. 하천시설물",
          details: [
            { description: "(1) 하천제방상의 수문", period: 5 },
            { description: "(2) (1) 외의 시설", period: 3 },
          ],
        },
        {
          subCategory: "차. 관개수로 또는 매립",
          details: [{ description: "", period: 3 }],
        },
        {
          subCategory: "카. 부지정지",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "타. 조경시설물 또는 조경식재",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "파. 발전ㆍ가스 또는 산업설비",
          details: [
            { description: "(1) 철근콘크리트 또는 철골구조부", period: 7 },
            {
              description:
                "(2) 압력이 1㎠당 10kg 이상인 고압가스의 관로(부대기기를 포함) 설치",
              period: 3,
            },
            { description: "(3) (1) 및 (2) 외의 시설", period: 3 },
          ],
        },
        {
          subCategory: "하. 기타 토목공사",
          details: [{ description: "", period: 1 }],
        },
        {
          subCategory: "거. 건축",
          details: [
            {
              description: "(1) 대형 공공성 건축물의 기둥 또는 내력벽",
              period: 10,
            },
            {
              description:
                "(2) 대형 공공성 건축물 중 기둥 내력벽외의 주요구조부 또는 (1) 외의 건축물중 주요구조부",
              period: 5,
            },
            {
              description:
                "(3) 건축물 중 (1) 및 (2)와 너목의 전문공사를 제외한 기타 부분",
              period: 1,
            },
          ],
        },
        {
          subCategory: "너. 전문공사",
          details: [
            { description: "(1) 실내의장", period: 1 },
            { description: "(2) 토공", period: 2 },
            { description: "(3) 미장ㆍ타일", period: 1 },
            { description: "(4) 방수", period: 3 },
            { description: "(5) 도장", period: 1 },
            { description: "(6) 석공사 또는 조적", period: 2 },
            { description: "(7) 창호 설치", period: 1 },
            { description: "(8) 지붕", period: 3 },
            { description: "(9) 철물", period: 2 },
            { description: "(10) 철근콘크리트", period: 3 },
            {
              description:
                "(11) 급배수ㆍ공동구ㆍ지하저수조ㆍ냉난방기ㆍ환기ㆍ공기조화ㆍ자동제어ㆍ가스 또는 배연설비",
              period: 2,
            },
            { description: "(12) 승강기 또는 인양기기설비", period: 3 },
            { description: "(13) 온실설치공사", period: 2 },
            { description: "(14) 보우링그라우팅공사", period: 1 },
            { description: "(15) 건축물 조립공사", period: 1 },
            { description: "(16) 판금", period: 1 },
            { description: "(17) (11) 내지 (16) 외의 건물내 설비", period: 1 },
          ],
        },
      ],
    },
    {
      category: "전기공사업법에 의한 전기공사",
      items: [
        {
          subCategory: "가. 터널식 및 개착식전력구 송전설비공사(지중)",
          details: [
            { description: "(1) 철근콘크리트 또는 철골구조부", period: 10 },
            { description: "(2) (1) 외의 시설", period: 5 },
          ],
        },
        {
          subCategory: "나. 송배전관로 공사(지중)",
          details: [
            { description: "(1) 송전관로공사", period: 3 },
            { description: "(2) 배전관로공사", period: 2 },
          ],
        },
        {
          subCategory: "다. 154KW급이상 철탑공사",
          details: [{ description: "", period: 3 }],
        },
        {
          subCategory: "라. 변전소 설비(전기설비 및 기기설치공사)",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "마. 배전설비공사",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "바. 기타 전기공사",
          details: [{ description: "", period: 1 }],
        },
      ],
    },
    {
      category: "전기통신공사업법에 의한 전기통신공사",
      items: [
        {
          subCategory: "가. 통신공구공사(터널식 제외)",
          details: [{ description: "", period: 3 }],
        },
        {
          subCategory:
            "나. 통신시설용 철탑, 케이블 설치공사 및 지하관로공사 교환기 등 통신시설용 기계설치 공사",
          details: [{ description: "", period: 2 }],
        },
        {
          subCategory: "다. 기타 전기통신공사",
          details: [{ description: "", period: 1 }],
        },
      ],
    },
    {
      category: "소방법에 의한 소방공사 등",
      items: [
        {
          subCategory: "가. 소방법에 의한 소방공사 등",
          details: [{ description: "", period: 1 }],
        },
      ],
    },
  ];

  // 각 타입별 대표 필드를 반환하는 함수
  const getTitleField = (type: string, data: any): string => {
    switch (type) {
      case "companyInfo":
        return data.companyName || "새 회사정보";
      case "fieldRepresentative":
        return data.name || "새 현장대리인";
      case "projectInfo":
        return data.projectName || "새 공사정보";
      default:
        return "새 기본정보";
    }
  };

  const handleResidentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    // 숫자 입력만 허용
    if (
      !/^[0-9]$/.test(key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)
    ) {
      e.preventDefault();
      return;
    }

    // 숫자 입력 처리
    if (/^[0-9]$/.test(key)) {
      e.preventDefault(); // 기본 입력 막기

      // 현재 원본 주민번호에 새 숫자 추가
      let newNumbers = originalResidentNumber + key;

      // 13자리 초과시 무시
      if (newNumbers.length > 13) {
        return;
      }

      // 원본 저장
      setOriginalResidentNumber(newNumbers);

      // 원본값 그대로 표시 (마스킹 제거)
      let originalValue = "";
      if (newNumbers.length <= 6) {
        originalValue = newNumbers;
      } else if (newNumbers.length === 7) {
        originalValue =
          newNumbers.substring(0, 6) + "-" + newNumbers.substring(6);
      } else {
        originalValue =
          newNumbers.substring(0, 6) + "-" + newNumbers.substring(6);
      }

      // 폼 데이터 업데이트
      setFormData({
        ...formData,
        residentNumber: originalValue,
      });
    }

    // 백스페이스 처리
    if (key === "Backspace") {
      e.preventDefault();

      if (originalResidentNumber.length > 0) {
        const newNumbers = originalResidentNumber.slice(0, -1);
        setOriginalResidentNumber(newNumbers);

        // 원본값 그대로 표시 (마스킹 제거)
        let originalValue = "";
        if (newNumbers.length <= 6) {
          originalValue = newNumbers;
        } else if (newNumbers.length === 7) {
          originalValue =
            newNumbers.substring(0, 6) + "-" + newNumbers.substring(6);
        } else {
          originalValue =
            newNumbers.substring(0, 6) + "-" + newNumbers.substring(6);
        }

        setFormData({
          ...formData,
          residentNumber: originalValue,
        });
      }
    }
  };

  const handleUnitFieldKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    fieldKey: string
  ) => {
    const key = e.key;
    const target = e.currentTarget;

    // 숫자, 백스페이스, 화살표, 탭만 허용
    if (
      !/^[0-9]$/.test(key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)
    ) {
      e.preventDefault();
      return;
    }

    // 백스페이스 처리 - 커서 위치 고려
    if (key === "Backspace") {
      const currentValue = target.value;
      const cursorPosition = target.selectionStart || 0;
      const numberOnly = currentValue.replace(/[^0-9]/g, "");

      // 커서가 단위 부분(년, 일)에 있거나 단위 바로 앞에 있을 때
      if (cursorPosition >= numberOnly.length) {
        e.preventDefault();

        if (numberOnly.length <= 1) {
          // 숫자가 1자리 이하면 완전히 지우기
          setFormData({
            ...formData,
            [fieldKey]: "",
          });
        } else {
          // 마지막 숫자 한 자리만 지우기
          const newNumber = numberOnly.slice(0, -1);
          const unit = fieldKey === "projectPeriod" ? "일" : "년";
          setFormData({
            ...formData,
            [fieldKey]: newNumber + unit,
          });
        }
      }
      // 커서가 숫자 부분에 있으면 기본 백스페이스 동작 허용
    }
  };

  // 착공일 변경 핸들러
  const handleStartDateChange = (value: string) => {
    setDateError("");

    // 착공일이 준공일보다 늦으면 준공일을 자동으로 조정
    if (value && formData.completionDate && value > formData.completionDate) {
      setFormData({
        ...formData,
        startDate: value,
        completionDate: value,
      });
      setDateError(
        "착공일이 준공일보다 늦어서 준공일을 자동으로 조정했습니다."
      );
      setTimeout(() => setDateError(""), 3000); // 3초 후 에러 메시지 제거
    } else {
      setFormData({
        ...formData,
        startDate: value,
      });
    }
  };

  // 준공일 변경 핸들러
  const handleCompletionDateChange = (value: string) => {
    setDateError("");

    // 준공일이 착공일보다 빠르면 착공일을 자동으로 조정
    if (value && formData.startDate && value < formData.startDate) {
      setFormData({
        ...formData,
        startDate: value,
        completionDate: value,
      });
      setDateError(
        "준공일이 착공일보다 빨라서 착공일을 자동으로 조정했습니다."
      );
      setTimeout(() => setDateError(""), 3000); // 3초 후 에러 메시지 제거
    } else {
      setFormData({
        ...formData,
        completionDate: value,
      });
    }
  };

  const handleInputChange = (key: string, value: string) => {
    let processedValue = value;

    // 날짜 필드는 별도 핸들러에서 처리
    if (key === "startDate") {
      handleStartDateChange(value);
      return;
    } else if (key === "completionDate") {
      handleCompletionDateChange(value);
      return;
    }

    // 금액 관련 필드 (숫자만 입력, 콤마 추가, "원" 자동 추가)
    const currencyFields = [
      "contractAmount",
      "contractDeposit",
      "completionSettlementAmount",
      "claimAmount",
    ];

    // 기간 관련 필드 (숫자만 입력, "년" 자동 추가)
    const yearFields = ["warrantyPeriod", "defectWarrantyPeriod"];

    // 일수 관련 필드 (숫자만 입력, "일" 자동 추가)
    const dayFields = ["projectPeriod"];

    // 사업자번호 필드 (숫자만 입력, 하이픈 자동 추가)
    const businessNumberFields = ["businessNumber"];

    // 휴대폰번호 필드 (숫자만 입력, 하이픈 자동 추가) - 현장대리인 전용
    const phoneFields = ["phone"];

    // 회사전화 필드 (숫자만 입력, 하이픈 자동 추가, 지역번호별 구분) - 회사정보 전용
    const companyPhoneFields = ["phone"];

    // 팩스번호 필드 (숫자만 입력, 하이픈 자동 추가, 지역번호별 구분)
    const faxFields = ["fax"];

    // 법인번호 필드 (숫자만 입력, 하이픈 자동 추가)
    const corporateNumberFields = ["corporateNumber"];

    // 주민번호는 onKeyDown에서 처리하므로 여기서는 건너뜀
    if (key === "residentNumber") {
      return;
    } else if (businessNumberFields.includes(key)) {
      // 사업자번호: 숫자만 추출하여 XXX-XX-XXXXX 형식으로 변환
      const numberOnly = value.replace(/[^0-9]/g, "");
      if (numberOnly.length <= 3) {
        processedValue = numberOnly;
      } else if (numberOnly.length <= 5) {
        processedValue =
          numberOnly.substring(0, 3) + "-" + numberOnly.substring(3);
      } else {
        processedValue =
          numberOnly.substring(0, 3) +
          "-" +
          numberOnly.substring(3, 5) +
          "-" +
          numberOnly.substring(5, 10);
      }
    } else if (phoneFields.includes(key) && activeTab !== "companyInfo") {
      // 현장대리인 휴대폰번호: 숫자만 추출하여 XXX-XXXX-XXXX 형식으로 변환
      const numberOnly = value.replace(/[^0-9]/g, "");
      if (numberOnly.length <= 3) {
        processedValue = numberOnly;
      } else if (numberOnly.length <= 7) {
        processedValue =
          numberOnly.substring(0, 3) + "-" + numberOnly.substring(3);
      } else {
        processedValue =
          numberOnly.substring(0, 3) +
          "-" +
          numberOnly.substring(3, 7) +
          "-" +
          numberOnly.substring(7, 11);
      }
    } else if (
      companyPhoneFields.includes(key) &&
      activeTab === "companyInfo"
    ) {
      // 회사전화: 숫자와 하이픈만 허용
      processedValue = value.replace(/[^0-9-]/g, "");
    } else if (faxFields.includes(key)) {
      // 팩스번호: 숫자와 하이픈만 허용
      processedValue = value.replace(/[^0-9-]/g, "");
    } else if (corporateNumberFields.includes(key)) {
      // 법인번호: 숫자만 추출하여 XXXXXX-XXXXXXX 형식으로 변환
      const numberOnly = value.replace(/[^0-9]/g, "");
      if (numberOnly.length <= 6) {
        processedValue = numberOnly;
      } else {
        processedValue =
          numberOnly.substring(0, 6) + "-" + numberOnly.substring(6, 13);
      }
    } else if (currencyFields.includes(key)) {
      // 숫자만 추출 (콤마 제거)
      const numberOnly = value.replace(/[^0-9]/g, "");
      if (numberOnly) {
        // 콤마만 추가 (원 제거)
        const formattedNumber = parseInt(numberOnly).toLocaleString("ko-KR");
        processedValue = formattedNumber;
      } else {
        processedValue = "";
      }
    } else if (yearFields.includes(key)) {
      // 숫자만 추출 ("년" 제거)
      const numberOnly = value.replace(/[^0-9]/g, "");
      // 빈 문자열이거나 0일 때는 단위 없이 처리
      if (numberOnly && numberOnly !== "0") {
        processedValue = numberOnly + "년";
      } else {
        processedValue = numberOnly; // "0"이거나 빈 문자열인 경우 단위 없음
      }
    } else if (dayFields.includes(key)) {
      // 숫자만 추출 ("일" 제거)
      const numberOnly = value.replace(/[^0-9]/g, "");
      // 빈 문자열이거나 0일 때는 단위 없이 처리
      if (numberOnly && numberOnly !== "0") {
        processedValue = numberOnly + "일";
      } else {
        processedValue = numberOnly; // "0"이거나 빈 문자열인 경우 단위 없음
      }
    }
    // 대표자명과 성명은 화면에서는 그대로 표시 (백엔드로만 공백 처리)

    setFormData({
      ...formData,
      [key]: processedValue,
    });
  };

  const handleSave = () => {
    // 현장대리인이나 공사정보 저장 시 회사정보 필수 체크
    if (activeTab === "fieldRepresentative" || activeTab === "projectInfo") {
      if (!selectedCompanyInfo) {
        alert("회사정보를 먼저 선택해주세요.");
        // 회사정보 선택 필드에 포커스
        const companySelectElement = document.querySelector(
          'select[data-field-key="companyInfo"]'
        ) as HTMLSelectElement;
        if (companySelectElement) {
          companySelectElement.focus();
          companySelectElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }
    }

    // 모든 필드 검증 (빈 값 체크) - 화면에 표시되지 않는 필드는 제외
    const hiddenFields = [
      "performancePeriod",
      "projectPeriod",
      "warrantyPeriod",
    ]; // 화면에 숨겨진 필드들
    const emptyFields = presetConfig.fields.filter((field) => {
      // 화면에 숨겨진 필드는 검증에서 제외
      if (hiddenFields.includes(field.key)) {
        return false;
      }

      const value = formData[field.key];
      // 값이 없거나, 문자열인 경우 trim했을 때 빈 문자열인 경우
      return !value || (typeof value === "string" && value.trim() === "");
    });

    if (emptyFields.length > 0) {
      const firstEmptyField = emptyFields[0];
      alert(`${firstEmptyField.label}을(를) 입력해주세요.`);

      // 첫 번째 빈 필드에 포커스
      const inputElement = document.querySelector(
        `input[data-field-key="${firstEmptyField.key}"]`
      ) as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
        inputElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // 공사정보인 경우 공사이행기간과 보증기간 자동 계산
    if (activeTab === "projectInfo") {
      const contractDate = formData.contractDate;
      const completionDate = formData.completionDate;

      if (contractDate && completionDate) {
        // 날짜 형식 변환 함수
        const formatDateToKorean = (dateStr: string) => {
          if (dateStr.includes("-") && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts = dateStr.split("-");
            if (parts.length === 3) {
              const year = parts[0];
              const month = parts[1];
              const day = parts[2];
              return `${year}년 ${month}월 ${day}일`;
            }
          }
          return dateStr;
        };

        // 공사기간 계산: 계약일자 ~ 준공일자 사이의 일수 계산
        const contractDateObj = new Date(contractDate);
        const completionDateObj = new Date(completionDate);
        const timeDiff =
          completionDateObj.getTime() - contractDateObj.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        if (daysDiff > 0) {
          formData.projectPeriod = `${daysDiff}일`;
        }

        // 공사이행기간 계산: 착공일자 ~ 준공일자 (한국어 형식)
        const formattedStartDate = formatDateToKorean(formData.startDate);
        const formattedCompletionDate = formatDateToKorean(completionDate);
        if (formData.startDate && completionDate) {
          formData.performancePeriod = `${formattedStartDate} ~ ${formattedCompletionDate}`;
        }

        // 보증기간 계산: 계약일자 ~ 준공일자 (한국어 형식)
        const formattedContractDate = formatDateToKorean(contractDate);
        formData.warrantyPeriod = `${formattedContractDate} ~ ${formattedCompletionDate}`;
      }
    }

    // 현장대리인이나 공사정보인 경우 회사정보 ID 추가
    if (activeTab === "fieldRepresentative" || activeTab === "projectInfo") {
      if (selectedCompanyInfo) {
        formData.companyId = selectedCompanyInfo.id.split("-")[0];
      }
    }

    // 백엔드로 보내기 전에 데이터 형식 변환
    const formDataForBackend = { ...formData };

    // 주민번호 원본값 처리 (마스킹된 값 대신 원본값 사용)
    if (originalResidentNumber && activeTab === "fieldRepresentative") {
      // 원본 주민번호에 하이픈 추가하여 올바른 형식으로 변환
      if (originalResidentNumber.length === 13) {
        formDataForBackend.residentNumber =
          originalResidentNumber.substring(0, 6) +
          "-" +
          originalResidentNumber.substring(6);
      } else {
        formDataForBackend.residentNumber = originalResidentNumber;
      }
    }

    // 날짜 형식 변환 (yyyy-mm-dd → yyyy년 mm월 dd일)
    const dateFields = [
      "contractDate",
      "startDate",
      "completionDate",
      "expectedCompletionDate",
      "claimDate",
      "birthDate",
      "registrationDate",
      "year",
    ];

    dateFields.forEach((field) => {
      const formDataAny = formDataForBackend as any;
      if (formDataAny[field] && typeof formDataAny[field] === "string") {
        const dateValue = formDataAny[field] as string;
        // yyyy-mm-dd 형식을 yyyy년 mm월 dd일로 변환
        if (dateValue.includes("-") && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const parts = dateValue.split("-");
          if (parts.length === 3) {
            const year = parts[0];
            const month = parts[1];
            const day = parts[2];
            formDataAny[field] = `${year}년 ${month}월 ${day}일`;
          }
        }
      }
    });

    // 하자보증기간에서 "년" 단위 제거 (백엔드 전송용)
    if (
      formDataForBackend.defectWarrantyPeriod &&
      typeof formDataForBackend.defectWarrantyPeriod === "string"
    ) {
      formDataForBackend.defectWarrantyPeriod =
        formDataForBackend.defectWarrantyPeriod.replace(/년$/, "");
    }

    const presetToSave: Omit<PresetItem, "id" | "createdAt"> = {
      name: getTitleField(activeTab, formDataForBackend),
      type: activeTab,
      data: formDataForBackend,
    };

    // 편집 모드인 경우 id와 createdAt을 별도로 전달
    if (editingPreset) {
      onSave(presetToSave, {
        id: editingPreset.id,
        createdAt: editingPreset.createdAt,
      });

      // 편집된 프리셋이 로컬스토리지의 기본 프리셋인 경우 로컬스토리지도 업데이트
      if (getDefaultPresetsFromStorage && saveDefaultPresetsToStorage) {
        const currentDefaults = getDefaultPresetsFromStorage();
        const defaultPresetForType = currentDefaults[activeTab];

        // 현재 편집 중인 프리셋이 기본 프리셋으로 설정되어 있는 경우
        if (
          defaultPresetForType &&
          defaultPresetForType.id === editingPreset.id
        ) {
          // 업데이트된 프리셋 정보로 로컬스토리지 갱신
          const updatedPreset = {
            id: editingPreset.id,
            name: presetToSave.name,
            type: presetToSave.type,
            data: presetToSave.data,
            createdAt: editingPreset.createdAt,
          };

          const updatedDefaults = {
            ...currentDefaults,
            [activeTab]: updatedPreset,
          };

          saveDefaultPresetsToStorage(updatedDefaults);
        }
      }
    } else {
      // 새로운 프리셋 생성
      onSave(presetToSave);

      // 새로 생성된 회사정보는 자동으로 선택
      if (activeTab === "companyInfo") {
        // 저장 성공 후 새로 생성될 프리셋을 selectedCompanyInfo로 설정
        // 실제로는 부모 컴포넌트에서 프리셋 목록이 업데이트된 후에 처리되어야 함
      }
    }

    // 편집 모드인 경우 - 저장 후 최신 데이터로 폼 다시 로드
    if (editingPreset) {
      // 업데이트된 프리셋 정보 생성
      const updatedPreset = {
        id: editingPreset.id,
        name: presetToSave.name,
        type: presetToSave.type,
        data: presetToSave.data,
        createdAt: editingPreset.createdAt,
      };

      // 편집 중인 프리셋을 업데이트된 데이터로 교체
      onEditPreset(updatedPreset);

      // 폼 데이터도 저장된 데이터로 업데이트 (날짜 형식 다시 변환)
      const convertedData = { ...presetToSave.data };
      const dateFields = [
        "contractDate",
        "startDate",
        "completionDate",
        "expectedCompletionDate",
        "claimDate",
        "birthDate",
        "registrationDate",
        "year",
      ];

      dateFields.forEach((field) => {
        const convertedDataAny = convertedData as any;
        if (
          convertedDataAny[field] &&
          typeof convertedDataAny[field] === "string"
        ) {
          const dateValue = convertedDataAny[field] as string;
          // yyyy년 mm월 dd일 형식을 yyyy-mm-dd로 변환
          if (
            dateValue.includes("년") &&
            dateValue.includes("월") &&
            dateValue.includes("일")
          ) {
            const yearMatch = dateValue.match(/(\d{4})년/);
            const monthMatch = dateValue.match(/(\d{1,2})월/);
            const dayMatch = dateValue.match(/(\d{1,2})일/);

            if (yearMatch && monthMatch && dayMatch) {
              const year = yearMatch[1];
              const month = monthMatch[1].padStart(2, "0");
              const day = dayMatch[1].padStart(2, "0");
              convertedDataAny[field] = `${year}-${month}-${day}`;
            }
          }
        }
      });

      setFormData(convertedData);

      // 공사정보인 경우 회사정보 연결 유지
      if (updatedPreset.type === "projectInfo") {
        const data = updatedPreset.data as any;
        console.log("💾 저장 후 회사정보 연결 - companyId:", data.companyId);

        if (data.companyId) {
          console.log("🔍 저장 후 검색 조건 - data.companyId:", data.companyId);
          console.log(
            "🔍 저장 후 회사정보 프리셋 ID들:",
            presets
              .filter((p) => p.type === "companyInfo")
              .map((p) => ({
                fullId: p.id,
                splitId: p.id.split("-")[0],
                name: p.name,
              }))
          );

          const companyPreset = presets.find(
            (p) =>
              p.type === "companyInfo" &&
              p.id.split("-")[0] === String(data.companyId)
          );
          console.log("🔗 저장 후 찾은 회사정보 프리셋:", companyPreset);
          setSelectedCompanyInfo(companyPreset || null);
        }
      }
    } else {
      // 새로운 프리셋 생성인 경우 - 목록으로 돌아가기
      setShowForm(false);
      setIsEditing(false);
      handleReset();
    }
  };

  const handleReset = () => {
    const initialData: any = {};
    PRESET_TYPES[activeTab].fields.forEach((field) => {
      initialData[field.key] = "";
    });
    setFormData(initialData);
    setIsEditing(false);
    setShowForm(false);
    setOriginalResidentNumber("");
    setDateError(""); // 날짜 에러 메시지 초기화

    // 회사정보 탭이 아닌 경우에만 선택된 회사정보를 초기화하지 않음
    if (activeTab === "companyInfo") {
      setSelectedCompanyInfo(null);
    }
  };

  const handleClose = () => {
    handleReset();
    setSelectedCompanyInfo(null);
    setDateError(""); // 날짜 에러 메시지 초기화
    onEditPreset(null); // 부모 컴포넌트의 editingPreset 초기화
    onClose();
  };

  const handleEditClick = (preset: PresetItem) => {
    setForceListMode(false); // 편집 모드 진입 시 forceListMode 해제
    onEditPreset(preset); // 부모 컴포넌트의 editingPreset 설정
  };

  const handleDeleteClick = (preset: PresetItem) => {
    // 삭제되는 회사정보가 현재 선택된 회사정보인 경우 선택 해제
    if (
      preset.type === "companyInfo" &&
      selectedCompanyInfo?.id === preset.id
    ) {
      setSelectedCompanyInfo(null);
    }

    onDeletePreset(preset);
    if (isEditing && editingPreset && editingPreset.id === preset.id) {
      handleReset();
    }
  };

  const handleAddNewClick = () => {
    setForceListMode(false); // 새 기본정보 추가 시 forceListMode 해제
    setIsEditing(false);
    const initialData: any = {};
    PRESET_TYPES[activeTab].fields.forEach((field) => {
      initialData[field.key] = "";
    });
    setFormData(initialData);
    setDateError(""); // 날짜 에러 메시지 초기화
    setShowForm(true);
  };

  const handleBackToList = () => {
    // 즉시 부모의 editingPreset 초기화 (가장 중요!)
    onEditPreset(null);

    // 강제 목록 모드 활성화 (다른 useEffect 간섭 방지)
    setForceListMode(true);
    setShowForm(false);
    setIsEditing(false);

    const initialData: any = {};
    PRESET_TYPES[activeTab].fields.forEach((field) => {
      initialData[field.key] = "";
    });
    setFormData(initialData);
    setOriginalResidentNumber("");
    setDateError(""); // 날짜 에러 메시지 초기화
    setSelectedCompanyInfo(null); // 선택된 회사정보도 초기화

    // forceListMode는 editingPreset이 null이 될 때 자동으로 해제됨 (위의 useEffect에서)
  };

  const handleWarrantyPeriodSelect = (period: string) => {
    // 선택된 기간에 "년" 단위 추가
    const formattedPeriod = period.includes("년") ? period : period + "년";
    handleInputChange("defectWarrantyPeriod", formattedPeriod);
  };

  const getPlaceholder = (key: string, label: string): string => {
    const placeholders: { [key: string]: string } = {
      // 회사정보
      businessNumber: "예) 000-00-00000",
      corporateNumber: "예) 000000-0000000",
      companyName: "예) (주)한국건설",
      address: "예) 서울특별시 강남구 테헤란로 152",
      detailAddress: "예) 강남파이낸스센터 27층",
      representative: "예) 이철수",
      phone:
        activeTab === "companyInfo"
          ? '"-"를 포함해서 입력해주세요'
          : "예) 010-1234-5678",
      fax: '"-"를 포함해서 입력해주세요',
      accountNumber: '"-"를 포함해서 입력해주세요',
      depositType: "예) 보통예금",
      accountHolder: "예) (주)한국건설",
      bankName: "예) 국민은행",
      branchName: "예) 강남대로지점",

      // 현장대리인
      name: "현장대리인 성명을 작성 하세요",
      residentNumber: "현장대리인 주민등록번호를 입력하세요",
      birthDate: "현장대리인 생년월일 입력",
      year: "자격증 취득/갱신일자를 선택하세요",
      period: "2015년 4월 1일 부터 2021년 4월 1일 현재까지",
      position: "직급을 입력하세요",
      category: "예) 토목기사, 조경기능사, 건축기사",
      grade: "초급, 중급, 고급, 특급",
      registrationNumber: "0909099119B(등록번호 입력)",
      registrationDate: "자격증 등록일자 입력",

      // 공사정보
      projectName: "서울시 아스팔트 포장공사",
      projectLocation: "현장주소를 입력해주세요",
      contractDate: "계약 기간을 선택해주세요",
      startDate: "착공 기간을 선택해주세요",
      completionDate: "준공 기간을 선택해주세요",
      projectPeriod: "예) 00일",
      performancePeriod: "예) 0000년 00월 00일 ~ 0000년 00월 00일",
      expectedCompletionDate: "예) 0000-00-00",
      contractAmount: "계약 금액을 입력해주세요",
      contractDeposit: "계약금액에서 보증금을 계산하여 입력하세요",
      warrantyPeriod: "예) 0년",
      client: "서울시 재무관",
      businessType: "예) 포장공사업, 석공사업, 실내건축",
      defectWarrantyPeriod: "예) 0년",
      cityInfo: "서울시, 부산시, 제주시 읍면동은 소속시 작성",
      orderingOrganization: "예) 경기도 이천시, 제주도 제주시",
      completionSettlementAmount: "정산된 준공금액을 입력하세요",
      claimDate: "청구 기간을 선택하세요",
      claimAmount: "청구금액 선급금과 준공정산금액 유의하세요",
    };

    // 주소 필드는 타입에 따라 다른 예시 사용
    if (key === "address") {
      if (activeTab === "companyInfo") {
        return "예) 서울특별시 강남구 테헤란로 152";
      } else if (activeTab === "fieldRepresentative") {
        return "예) 인천광역시 남동구 구월로 123번길 45";
      }
    }

    return placeholders[key] || `${label} 입력`;
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      disableBackdropClick={true}
      className="d-flex align-items-center justify-content-center"
    >
      <div
        className="bg-white rounded-4 p-4 mx-3"
        style={{
          maxWidth: "900px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          outline: "none",
        }}
      >
        {/* 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h5 className="fw-bold mb-1">기본정보 관리</h5>
            <p className="text-muted mb-0">자주 사용하는 정보를 관리하세요.</p>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={onClose}
              className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "18px" }}
              >
                close
              </span>
            </button>
          </div>
        </div>

        {forceListMode || (!showForm && !editingPreset) ? (
          /* 프리셋 목록 화면 */
          <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h6 className="fw-semibold mb-0">등록된 기본정보</h6>
              <button
                onClick={handleAddNewClick}
                className="btn btn-primary d-flex align-items-center"
                style={{ borderRadius: "8px" }}
              >
                <span className="material-symbols-outlined me-2">add</span>
                기본정보 추가
              </button>
            </div>

            {/* 탭 버튼 */}
            <div className="d-flex flex-wrap gap-2 mb-4">
              {Object.entries(PRESET_TYPES).map(([key, config]) => {
                // 현장대리인과 공사정보 탭은 회사정보가 등록되어 있을 때만 활성화
                const isTabDisabled =
                  !showForm &&
                  (key === "fieldRepresentative" || key === "projectInfo") &&
                  presets.filter((p) => p.type === "companyInfo").length === 0;

                return (
                  <button
                    key={key}
                    className={`btn ${
                      activeTab === key
                        ? "btn-primary shadow-sm"
                        : isTabDisabled
                        ? "btn-outline-secondary disabled"
                        : "btn-outline-secondary"
                    } d-flex align-items-center position-relative`}
                    onClick={() => !isTabDisabled && setActiveTab(key as any)}
                    disabled={isTabDisabled}
                    style={{
                      fontSize: "0.9rem",
                      borderRadius: "10px",
                      transition: "all 0.2s ease",
                      minWidth: "140px",
                      justifyContent: "center",
                      fontWeight: activeTab === key ? "600" : "500",
                      cursor: isTabDisabled ? "not-allowed" : "pointer",
                      opacity: isTabDisabled ? 0.6 : 1,
                    }}
                    title={
                      isTabDisabled ? "회사정보를 먼저 등록해주세요" : undefined
                    }
                  >
                    <span
                      className="material-symbols-outlined me-2"
                      style={{ fontSize: "18px" }}
                    >
                      {config.icon}
                    </span>
                    {config.label}
                    <span className="badge bg-light text-dark ms-2">
                      {presets.filter((p) => p.type === key).length}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* 선택된 탭의 프리셋 목록 */}
            <div style={{ minHeight: "300px" }}>
              {presets.filter((p) => p.type === activeTab).length > 0 ? (
                <div className="row g-3">
                  {presets
                    .filter((p) => p.type === activeTab)
                    .map((preset) => (
                      <div key={preset.id} className="col-md-6">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between mb-2">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-1">
                                  <h5 className="card-title fw-bold mb-0 me-2">
                                    {getTitleField(preset.type, preset.data)}
                                  </h5>
                                </div>
                                <small className="text-muted">
                                  {PRESET_TYPES[activeTab].label} 기본정보
                                  {/* 현장대리인이나 공사정보일 때 연결된 회사정보 표시 */}
                                  {(preset.type === "fieldRepresentative" ||
                                    preset.type === "projectInfo") &&
                                    (preset.data as any).companyId && (
                                      <>
                                        <br />
                                        <span className="text-info">
                                          연결된 회사:{" "}
                                          {(() => {
                                            return getLinkedCompanyName(
                                              (preset.data as any).companyId
                                            );
                                          })()}
                                        </span>
                                      </>
                                    )}
                                </small>
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  onClick={() => handleEditClick(preset)}
                                  className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                                  style={{
                                    borderRadius: "6px",
                                    width: "28px",
                                    height: "28px",
                                    padding: "0",
                                  }}
                                  title="편집"
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: "14px" }}
                                  >
                                    edit
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(preset)}
                                  className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                                  style={{
                                    borderRadius: "6px",
                                    width: "28px",
                                    height: "28px",
                                    padding: "0",
                                  }}
                                  title="삭제"
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: "14px" }}
                                  >
                                    delete
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span
                      className="material-symbols-outlined text-muted"
                      style={{ fontSize: "4rem" }}
                    >
                      {PRESET_TYPES[activeTab].icon}
                    </span>
                  </div>
                  <h6 className="text-muted mb-2">
                    등록된 {PRESET_TYPES[activeTab].label} 기본정보가 없습니다
                  </h6>
                  <p className="text-muted small mb-3">
                    새로운 기본정보를 추가하여 효율적으로 작업하세요
                  </p>
                  <button
                    onClick={handleAddNewClick}
                    className="btn btn-primary"
                    style={{ borderRadius: "8px" }}
                  >
                    <span className="material-symbols-outlined me-2">add</span>
                    첫 번째 기본정보 추가하기
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* 프리셋 폼 화면 */
          <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center">
                <button
                  onClick={handleBackToList}
                  className="btn btn-outline-secondary btn-sm me-3"
                  style={{ borderRadius: "8px" }}
                >
                  <span className="material-symbols-outlined me-1">
                    arrow_back
                  </span>
                  목록으로
                </button>
                <h6 className="fw-semibold mb-0">
                  {isEditing ? "기본정보 편집" : "새 기본정보 추가"}
                  <span className="text-muted ms-2">
                    - {PRESET_TYPES[activeTab].label}
                  </span>
                </h6>
              </div>
            </div>

            <div className="row">
              <div className="col-md-8 mx-auto">
                {/* 회사정보 선택 (현장대리인/공사정보 폼에서만 표시) */}
                {(activeTab === "fieldRepresentative" ||
                  activeTab === "projectInfo") && (
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="card-title fw-semibold mb-3">
                        연결할 회사정보 선택
                      </h6>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label fw-semibold">
                            회사정보
                          </label>
                          <select
                            className="form-control"
                            value={selectedCompanyInfo?.id || ""}
                            onChange={(e) => {
                              const companyId = e.target.value;
                              const company = presets.find(
                                (p) => p.id === companyId
                              );
                              setSelectedCompanyInfo(company || null);
                            }}
                            data-field-key="companyInfo"
                            style={{ borderRadius: "8px" }}
                          >
                            <option value="">회사정보를 선택하세요</option>
                            {presets
                              .filter((p) => p.type === "companyInfo")
                              .map((company) => (
                                <option key={company.id} value={company.id}>
                                  {company.name}
                                </option>
                              ))}
                          </select>
                          {selectedCompanyInfo && (
                            <div className="mt-2">
                              <small className="text-muted">
                                선택된 회사: {selectedCompanyInfo.name}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 프리셋 데이터 입력 필드들 */}
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h6 className="card-title fw-semibold mb-3">
                      {PRESET_TYPES[activeTab].label}
                    </h6>

                    {/* 날짜 에러 메시지 */}
                    {dateError && activeTab === "projectInfo" && (
                      <div className="row mb-3">
                        <div className="col-12">
                          <div
                            className="alert alert-warning d-flex align-items-center"
                            role="alert"
                          >
                            <span className="material-symbols-outlined me-2">
                              warning
                            </span>
                            {dateError}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row g-3">
                      {PRESET_TYPES[activeTab].fields
                        .filter(
                          (field) =>
                            // performancePeriod, projectPeriod, warrantyPeriod 필드를 화면에서 숨김
                            field.key !== "performancePeriod" &&
                            field.key !== "projectPeriod" &&
                            field.key !== "warrantyPeriod"
                        )
                        .map((field) => (
                          <div
                            key={field.key}
                            className={`${
                              [
                                "address",
                                "detailAddress",
                                "projectName",
                                "projectLocation",
                              ].includes(field.key)
                                ? "col-12"
                                : "col-md-6"
                            }`}
                          >
                            <label className="form-label fw-semibold">
                              {field.label}
                            </label>
                            {field.key === "defectWarrantyPeriod" ? (
                              <div className="d-flex gap-2">
                                <input
                                  type={field.type}
                                  className="form-control"
                                  placeholder="숫자만 입력하세요 (예: 3년)"
                                  value={formData[field.key] || ""}
                                  onChange={(e) =>
                                    handleInputChange(field.key, e.target.value)
                                  }
                                  onKeyDown={(e) =>
                                    handleUnitFieldKeyDown(e, field.key)
                                  }
                                  data-field-key={field.key}
                                  style={{ borderRadius: "8px" }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-primary d-flex align-items-center px-3"
                                  onClick={() => setShowWarrantyModal(true)}
                                  style={{ borderRadius: "8px" }}
                                >
                                  <span
                                    className="material-symbols-outlined me-1"
                                    style={{ fontSize: "18px" }}
                                  >
                                    schedule
                                  </span>
                                  선택
                                </button>
                              </div>
                            ) : field.key === "residentNumber" ? (
                              <div className="d-flex gap-2 align-items-center">
                                <input
                                  type={field.type}
                                  className="form-control"
                                  placeholder={getPlaceholder(
                                    field.key,
                                    field.label
                                  )}
                                  value={getDisplayResidentNumber()}
                                  onChange={() => {}} // onChange는 onKeyDown에서 처리
                                  onKeyDown={(e) => handleResidentKeyDown(e)}
                                  data-field-key={field.key}
                                  style={{ borderRadius: "8px" }}
                                />
                              </div>
                            ) : (
                              <input
                                type={field.type}
                                className={`form-control ${
                                  dateError &&
                                  (field.key === "startDate" ||
                                    field.key === "completionDate")
                                    ? "border-warning"
                                    : ""
                                }`}
                                placeholder={getPlaceholder(
                                  field.key,
                                  field.label
                                )}
                                value={formData[field.key] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.key, e.target.value)
                                }
                                onKeyDown={
                                  [
                                    "warrantyPeriod",
                                    "defectWarrantyPeriod",
                                    "projectPeriod",
                                  ].includes(field.key)
                                    ? (e) =>
                                        handleUnitFieldKeyDown(e, field.key)
                                    : undefined
                                }
                                data-field-key={field.key}
                                style={{ borderRadius: "8px" }}
                              />
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                {/* 버튼 그룹 */}
                <div className="d-flex gap-3 mt-4">
                  <button
                    onClick={handleBackToList}
                    className="btn btn-outline-secondary flex-fill"
                    style={{ borderRadius: "10px" }}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary flex-fill"
                    style={{ borderRadius: "10px" }}
                  >
                    <span className="material-symbols-outlined me-2">save</span>
                    {isEditing ? "수정하기" : "저장하기"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 하자보증기간 선택 모달 */}
        <WarrantyPeriodModal
          isOpen={showWarrantyModal}
          onClose={() => setShowWarrantyModal(false)}
          onSelect={handleWarrantyPeriodSelect}
        />
      </div>
    </Modal>
  );
};

export default PresetModal;
